// Projeto: Haklai Church — Plugin WordPress Modular - Desenvolvido por: Ricardo Sarmento - https://linx.pt

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import AppLayout from '../components/AppLayout';
import Avatar from '../components/Avatar';
import { getRoleBadgeStyles } from '../utils/roleColors';
import ManageAccessModal from '../components/ManageAccessModal';

function MembersListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Estados para o Modal de Acesso
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  // Carrega todas as funções (roles) disponíveis
  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const response = await api.get('/roles');
      return response.data.data || [];
    },
  });

  // Filtra as funções para remover opções não executivas do combobox
  const filteredRoles = React.useMemo(() => {
    const rolesList = rolesData || [];
    const excludedSlugs = ['visitante', 'membro', 'frequentador', 'frequentador_assiduo'];
    return rolesList.filter(role => !excludedSlugs.includes(role.slug));
  }, [rolesData]);

  const EXECUTIVE_ROLE_SLUGS = [
    'lider_celula', 
    'discipulador_celula', 
    'pastor_rede', 
    'pastor_senior', 
    'pastor_supervisor', 
    'admin', 
    'tenant_admin'
  ];

  const isExecutive = (memberItem) => {
    return memberItem.roles && memberItem.roles.some(r => EXECUTIVE_ROLE_SLUGS.includes(r.slug));
  };

  const openManageModal = (memberItem) => {
    setSelectedMember(memberItem);
    setIsModalOpen(true);
  };

  const closeManageModal = () => {
    setIsModalOpen(false);
    setSelectedMember(null);
  };

  const handleModalSuccess = () => {
    closeManageModal();
    queryClient.invalidateQueries({ queryKey: ['members'] });
  };

  // Estados de filtros e paginação
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [cellFilter, setCellFilter] = useState('');
  const [skillFilter, setSkillFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedMembers, setSelectedMembers] = useState([]);

  // Debounce para busca
  const [searchDebounced, setSearchDebounced] = useState('');
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(1); // Reset para primeira página ao buscar
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Carrega células para filtro
  const { data: cellsData } = useQuery({
    queryKey: ['cells'],
    queryFn: async () => {
      const response = await api.get('/cells');
      return response.data.data || [];
    },
  });

  // Carrega habilidades para filtro (hardcoded conforme especificação)
  const skills = [
    { id: 1, name: 'Música' },
    { id: 2, name: 'Teatro e Dança' },
    { id: 3, name: 'Tecnologia' },
    { id: 4, name: 'Mídia e Comunicação' },
    { id: 5, name: 'Sonorização e Iluminação' },
    { id: 6, name: 'Recepção e Eventos' },
    { id: 7, name: 'Gastronomia' },
  ];

  // Carrega membros com filtros
  const { data: membersData, isLoading, error, refetch } = useQuery({
    queryKey: ['members', page, perPage, searchDebounced, categoryFilter, cellFilter, skillFilter, sortBy, sortOrder],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
        sort: sortBy,
        order: sortOrder,
      });

      if (searchDebounced) params.append('search', searchDebounced);
      if (categoryFilter) params.append('category', categoryFilter);
      if (cellFilter) params.append('cell_id', cellFilter);
      if (skillFilter) params.append('skill_id', skillFilter);

      const response = await api.get(`/members?${params.toString()}`);
      return response.data;
    },
  });

  const members = membersData?.data || [];
  const meta = membersData?.meta || {
    current_page: 1,
    per_page: 20,
    total: 0,
    total_pages: 1,
  };

  // Categorias disponíveis
  const categories = [
    { value: '', label: 'Todas as categorias' },
    { value: 'visitor', label: 'Visitante' },
    { value: 'frequent', label: 'Frequentador Assíduo' },
    { value: 'frequent_encounter', label: 'Frequentador + Encontro' },
    { value: 'member', label: 'Membro' },
  ];

  // Labels de categoria
  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  // Badge de categoria
  const getCategoryBadge = (category) => {
    const badges = {
      visitor: 'badge-visitor',
      frequent: 'badge-frequent',
      frequent_encounter: 'badge-encounter',
      member: 'badge-member',
    };
    return badges[category] || 'badge-gray';
  };

  // Toggle seleção de membro
  const toggleMemberSelection = (memberId) => {
    setSelectedMembers(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  // Selecionar todos
  const toggleSelectAll = () => {
    if (selectedMembers.length === members.length) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(members.map(m => m.id));
    }
  };

  // Mutation para alterar categoria em massa
  const bulkCategoryMutation = useMutation({
    mutationFn: async ({ memberIds, category }) => {
      const promises = memberIds.map(id =>
        api.put(`/members/${id}`, { category })
      );
      await Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      setSelectedMembers([]);
      alert('Categorias atualizadas com sucesso!');
    },
    onError: (error) => {
      alert('Erro ao atualizar categorias: ' + (error.response?.data?.message || error.message));
    },
  });

  // Handler para alterar categoria em massa
  const handleBulkCategoryChange = () => {
    if (selectedMembers.length === 0) {
      alert('Selecione pelo menos um membro');
      return;
    }
    const newCategory = prompt('Digite a nova categoria (visitor, frequent, frequent_encounter, member):');
    if (newCategory && ['visitor', 'frequent', 'frequent_encounter', 'member'].includes(newCategory)) {
      bulkCategoryMutation.mutate({ memberIds: selectedMembers, category: newCategory });
    }
  };

  // Handler para ordenação
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Ícone de ordenação
  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  if (isLoading && page === 1) {
    return (
      <AppLayout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Erro ao carregar membros</h2>
          <p>{error.message || 'Erro desconhecido'}</p>
          <button className="btn btn-primary" onClick={() => refetch()}>
            Tentar novamente
          </button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="members-list-page" style={{ padding: '2rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>Membros</h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            {meta.total} {meta.total === 1 ? 'membro encontrado' : 'membros encontrados'}
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/members/new')}
        >
          <span className="material-symbols-outlined" style={{ marginRight: '0.5rem' }}>add</span>
          Adicionar Membro
        </button>
      </div>

      {/* Filtros */}
      <div className="card" style={{ marginBottom: '1.5rem', padding: '1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          {/* Busca */}
          <div className="form-group">
            <label className="form-label">Buscar</label>
            <input
              type="text"
              className="form-input"
              placeholder="Nome, email ou telefone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Filtro de Categoria */}
          <div className="form-group">
            <label className="form-label">Categoria</label>
            <select
              className="form-input"
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPage(1);
              }}
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>

          {/* Filtro de Célula */}
          <div className="form-group">
            <label className="form-label">Célula</label>
            <select
              className="form-input"
              value={cellFilter}
              onChange={(e) => {
                setCellFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todas as células</option>
              {cellsData?.map(cell => (
                <option key={cell.id} value={cell.id}>{cell.name}</option>
              ))}
            </select>
          </div>

          {/* Filtro de Habilidade (apenas para membros) */}
          {categoryFilter === 'member' && (
            <div className="form-group">
              <label className="form-label">Habilidade</label>
              <select
                className="form-input"
                value={skillFilter}
                onChange={(e) => {
                  setSkillFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">Todas as habilidades</option>
                {skills.map(skill => (
                  <option key={skill.id} value={skill.id}>{skill.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Itens por página */}
          <div className="form-group">
            <label className="form-label">Por página</label>
            <select
              className="form-input"
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>

        {/* Ações em massa */}
        {selectedMembers.length > 0 && (
          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border)', display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <span style={{ color: 'var(--text-secondary)' }}>
              {selectedMembers.length} {selectedMembers.length === 1 ? 'membro selecionado' : 'membros selecionados'}
            </span>
            <button
              className="btn btn-secondary"
              onClick={handleBulkCategoryChange}
              disabled={bulkCategoryMutation.isPending}
            >
              Alterar Categoria
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => {
                // TODO: Implementar exportação
                alert('Exportação será implementada em breve');
              }}
            >
              Exportar Selecionados
            </button>
            <button
              className="btn btn-text"
              onClick={() => setSelectedMembers([])}
            >
              Limpar Seleção
            </button>
          </div>
        )}
      </div>

      {/* Tabela de Membros */}
      {members.length === 0 ? (
        <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
          <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
            people
          </span>
          <h3 style={{ marginBottom: '0.5rem' }}>Nenhum membro encontrado</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
            {searchDebounced || categoryFilter || cellFilter
              ? 'Tente ajustar os filtros de busca'
              : 'Comece adicionando seu primeiro membro'}
          </p>
          {!searchDebounced && !categoryFilter && !cellFilter && (
            <button
              className="btn btn-primary"
              onClick={() => navigate('/members/new')}
            >
              Adicionar Primeiro Membro
            </button>
          )}
        </div>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '0.75rem', textAlign: 'left', width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={selectedMembers.length === members.length && members.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th style={{ padding: '0.75rem', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('full_name')}>
                  Nome {getSortIcon('full_name')}
                </th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Email</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('category')}>
                  Categoria {getSortIcon('category')}
                </th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Células</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Status Acesso</th>
                <th style={{ padding: '0.75rem', textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {members.map(member => (
                <tr
                  key={member.id}
                  style={{
                    borderBottom: '1px solid var(--border)',
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate(`/members/${member.id}`)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--surface-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <td style={{ padding: '0.75rem' }} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedMembers.includes(member.id)}
                      onChange={() => toggleMemberSelection(member.id)}
                    />
                  </td>
                  <td style={{ padding: '0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <Avatar name={member.full_name} photo={member.photo_thumbnail_url} size="sm" />
                      <span style={{ fontWeight: '500' }}>{member.full_name}</span>
                    </div>
                  </td>
                  <td style={{ padding: '0.75rem', color: 'var(--text-secondary)' }}>
                    {member.email || '-'}
                  </td>
                   <td style={{ padding: '0.75rem' }}>
                    {member.category === 'member' && member.roles && member.roles.length > 0 ? (
                      <span className="badge" style={getRoleBadgeStyles(member.roles[0])}>
                        {member.roles[0].name}
                      </span>
                    ) : (
                      <span className={`badge ${getCategoryBadge(member.category)}`}>
                        {getCategoryLabel(member.category)}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem', color: 'var(--text-secondary)' }}>
                    {Array.isArray(member.cells) && member.cells.length > 0
                      ? member.cells.map(c => c.name).join(', ')
                      : '-'}
                  </td>
                  <td style={{ padding: '0.75rem' }}>
                    {member.wp_user_id ? (
                      <span className="badge badge-success" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>check_circle</span>
                        Ativo
                      </span>
                    ) : (
                      isExecutive(member) ? (
                        <span className="badge badge-warning">Sem Acesso</span>
                      ) : (
                        <span className="badge badge-gray" style={{ opacity: 0.6 }}>N/A</span>
                      )
                    )}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <button
                        className="btn btn-text btn-sm"
                        onClick={() => navigate(`/members/${member.id}`)}
                        title="Ver detalhes"
                      >
                        <span className="material-symbols-outlined">visibility</span>
                      </button>
                      <button
                        className="btn btn-text btn-sm"
                        onClick={() => navigate(`/members/${member.id}/edit`)}
                        title="Editar"
                      >
                        <span className="material-symbols-outlined">edit</span>
                      </button>
                      {isExecutive(member) && (
                        <>
                          {!member.wp_user_id ? (
                            <button
                              className="btn btn-text btn-sm text-primary"
                              onClick={(e) => { e.stopPropagation(); openManageModal(member); }}
                              title="Conceder Acesso"
                            >
                              <span className="material-symbols-outlined">vpn_key</span>
                            </button>
                          ) : (
                            <button
                              className="btn btn-text btn-sm text-primary"
                              onClick={(e) => { e.stopPropagation(); openManageModal(member); }}
                              title="Gerir Acesso"
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>key</span>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginação */}
      {meta.total > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem' }}>
          <div style={{ color: 'var(--text-secondary)' }}>
            Mostrando {((page - 1) * perPage) + 1} a {Math.min(page * perPage, meta.total)} de {meta.total} membros
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className="btn btn-secondary"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Anterior
            </button>
            {Array.from({ length: Math.min(5, meta.total_pages) }, (_, i) => {
              let pageNum;
              if (meta.total_pages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= meta.total_pages - 2) {
                pageNum = meta.total_pages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  className={`btn ${page === pageNum ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              className="btn btn-secondary"
              onClick={() => setPage(p => Math.min(meta.total_pages, p + 1))}
              disabled={page === meta.total_pages}
            >
              Próxima
            </button>
          </div>
        </div>
      )}

        {/* Modal Gerir Acesso */}
        <ManageAccessModal 
          isOpen={isModalOpen}
          onClose={closeManageModal}
          member={selectedMember}
          roles={filteredRoles}
          onSuccess={handleModalSuccess}
        />
      </div>
    </AppLayout>
  );
}

export default MembersListPage;

